import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import ButtonPrimary from 'components/shared/ButtonPrimary';
import ButtonText from 'components/shared/ButtonText';
import Column from 'components/shared/Column';
import Row from 'components/shared/Row';
import Text from 'components/shared/Text';
import Table from 'components/shared/Table';
import Value from 'components/shared/Value';
import Spinner from 'components/shared/Spinner';
import Blob from 'components/shared/Blob';
import Heading from 'components/shared/Heading';
import MarketId from 'components/shared/MarketId';
import ModalCancelOrder from 'components/modals/CancelOrder';
import { formatCreatedAt } from 'helpers/dates';
import { formatAmount } from '@nbx/frontend-helpers/format';
import calculateAverageUnitPrice from 'helpers/calculateAverageUnitPrice';
import { useOrder, useIsMobile } from 'helpers/hooks';
import useMarkets from 'hooks/useMarkets';
import useAssets from 'hooks/useAssets';
import { VIEW_STATUS } from 'constants/orders';

import Big from 'big.js';
import { useTranslation } from 'react-i18next';
import {
  orderIsNotCancellable,
  orderDataToStatus,
  isOpenOrder,
  mapFillsWithExtraData,
  getFillsQuantitySum,
  getCommissionsSum
} from 'helpers/orders';
import { ORDER_TYPE } from '../../constants/translations';
import styles from './styles.module.scss';

Big.RM = 0;

function OrderDetails(props) {
  const { t } = useTranslation();
  const orderId = props.match.params?.orderId;
  const order = useOrder(props.accountId, orderId);
  const markets = useMarkets({ disabled: true });
  const assets = useAssets();
  const isMobile = useIsMobile();

  const headers = isMobile
    ? [`${t('common_amount')} / ${t('common_price')}`, t('common_fee'), t('common_total')]
    : [t('common_price'), t('common_amount'), t('common_fee'), t('common_total'), t('orders_filled_at')];
  const columnWidths = isMobile ? [5, 3.5, 3.5] : [2, 2, 3, 2, 3];

  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const didCancel = useRef(false);

  const openCancelModal = () => setCancelModalOpen(true);
  const closeCancelModal = () => setCancelModalOpen(false);

  useEffect(() => {
    return () => (didCancel.current = true);
  }, []);

  const goBack = () => {
    window.HISTORY.goBack();
    // If going back doesn't do anything, close the tab
    setTimeout(() => {
      if (!didCancel.current) {
        window.close();
      }
    }, 150);
  };

  const header = (
    <>
      <div className={styles.pageTitleContainer}>
        <Heading tag={isMobile ? 'h3' : 'h1'} styles={[styles.pageTitle]}>
          {t('orders_order_details')}
        </Heading>
        {!order.loading && !order.error && isMobile && (
          <span
            className={classnames([
              styles.tag,
              {
                [styles.disabledTag]: [
                  VIEW_STATUS.CLOSED,
                  VIEW_STATUS.REJECTED,
                  VIEW_STATUS.PARTIALLY_FILLED_CLOSED
                ].includes(orderDataToStatus(order.data))
              }
            ])}>
            {orderDataToStatus(order.data)}
          </span>
        )}
      </div>
      {!order.loading && !order.error && !isMobile && (
        <ButtonText onClick={goBack} underlined>
          {t('common_back')}
        </ButtonText>
      )}

      {isMobile && <Text muted>{order.data.id}</Text>}
    </>
  );

  if (order.loading || markets.loading || assets.loading) {
    return (
      <div className={styles.orderDetails}>
        {header}
        <div className={styles.spinnerContainer}>
          <Spinner />
        </div>
      </div>
    );
  }

  if (order.error) {
    return (
      <div className={styles.orderDetails}>
        {header}
        <div className={styles.errorContainer}>
          <Blob styles={[styles.errorBlob]} />
          <Heading tag="h3" styles={[styles.noOrderHeading]}>
            {t('error_title_generic')}
          </Heading>
          <Text centered>
            {t('orders_we_were_unable')}{' '}
            <ButtonText onClick={order.refetch} underlined>
              {t('common_try_again')}
            </ButtonText>{' '}
            {t('common_or')}{' '}
            <ButtonText onClick={order.refetch} underlined>
              {t('common_go_back')}
            </ButtonText>
            .
          </Text>
        </div>
      </div>
    );
  }

  const isNotCancellable = orderIsNotCancellable(order.data.events);
  const baseAsset = !order.loading && order.data?.market.split('-')[0];
  const quoteAsset = !order.loading && order.data?.market.split('-')[1];

  let market = {};
  if (!markets.loading) {
    market = markets.data.find(marketInMarkets => marketInMarkets.id === order.data.market);
  }

  /** @fillWData - standard fill from the API response with some extra props: market & side. */
  const renderItem = (fillWData, index) => {
    const base = fillWData.market.split('-')[0];
    const quote = fillWData.market.split('-')[1];
    const subTotal = Big(fillWData.price).times(fillWData.quantity);
    const { fee } = fillWData;
    const total = fillWData.side === 'BUY' ? subTotal.plus(fee) : subTotal.minus(fee);

    return {
      0: (
        <Text monospaced>
          {formatAmount(fillWData.price, market.minTickLength)} {quote}
        </Text>
      ),
      1: (
        <Text monospaced>
          {formatAmount(fillWData.quantity, assets.data[market.baseCurrency].displayPrecision)} {base}
        </Text>
      ),
      2: (
        <Text monospaced>
          {fee} {quote}
        </Text>
      ),
      3: (
        <Text monospaced>
          {formatAmount(total, assets.data[market.quoteCurrency].displayPrecision)} {quote}
        </Text>
      ),
      4: <Text monospaced>{formatCreatedAt(fillWData.createdAt)}</Text>
    }[index];
  };

  /** @fillWData - standard fill from the API response with some extra props: market & side. */
  const renderItemMobile = (fillWData, index) => {
    const base = fillWData.market.split('-')[0];
    const quote = fillWData.market.split('-')[1];
    const subTotal = Big(fillWData.price).times(fillWData.quantity);
    const { fee } = fillWData;
    const total = fillWData.side === 'BUY' ? subTotal.plus(fee) : subTotal.minus(fee);

    return {
      0: (
        <Column>
          <Text monospaced>
            {formatAmount(fillWData.quantity, assets.data[market.baseCurrency].displayPrecision)} {base}
          </Text>
          <Text monospaced styles={[styles.renderItemDetail]} muted>
            @ {formatAmount(fillWData.price, market.minTickLength)} {quote}
          </Text>
        </Column>
      ),
      1: (
        <Column endAligned>
          <Text monospaced>
            {fee} {quote}
          </Text>
          <Text styles={[styles.renderItemDetail]}>&nbsp;</Text>
        </Column>
      ),
      2: (
        <Column endAligned>
          <Text monospaced>
            {formatAmount(total, assets.data[market.quoteCurrency].displayPrecision)} {quote}
          </Text>
          <Text styles={[styles.renderItemDetail]}>&nbsp;</Text>
        </Column>
      )
    }[index];
  };

  const executionPrice = calculateAverageUnitPrice(order.data);

  return (
    <div className={styles.orderDetails}>
      {header}
      {!isMobile ? (
        <>
          <Row styles={[styles.row]}>
            <Column lg={3}>
              <Text weight="medium">{t('common_status')}</Text>
            </Column>

            <Column lg={6}>
              <Text>{orderDataToStatus(order.data)}</Text>
            </Column>

            {isOpenOrder(order.data.events) && (
              <Column lg={3} endAligned>
                <ButtonPrimary
                  disabled={isNotCancellable}
                  title={isNotCancellable ? t('orders_cannot_be_cancelled') : undefined}
                  onClick={openCancelModal}
                  styles={[styles.buttonCancel]}>
                  {t('common_cancel_order')}
                </ButtonPrimary>
              </Column>
            )}
          </Row>

          <Row border={order.data.fills.length > 0} startAligned styles={[styles.row]}>
            <Column lg={3}>
              <Text weight="medium">{t('common_terms')}</Text>
            </Column>

            <Column fd="column" lg={6}>
              <Value name={t('common_order_id')}>{order.data.id}</Value>
              <Value name={t('common_pair')}>{order.data.market}</Value>
              <Value name={t('common_objects_type')}>
                {`
                  ${ORDER_TYPE(order.data.execution.type)}
                  ${order.data.side === 'BUY' ? t('common_buy') : t('common_sell')}
                  ${
                    order.data.execution.type === 'LIMIT' ? `${order.data.execution.price} ${quoteAsset}` : ''
                  }
                  `.toUpperCase()}
              </Value>
              <Value name={t('orders_execution_price')}>
                {executionPrice ? `${formatAmount(executionPrice, market.minTickLength)} ${quoteAsset}` : '-'}
              </Value>
              <Value name={t('common_order_amount')}>
                {formatAmount(order.data.quantity, assets.data[market.baseCurrency].displayPrecision)}{' '}
                {baseAsset}
              </Value>
              <Value name={t('common_filled_amount')}>
                {formatAmount(
                  getFillsQuantitySum(order.data),
                  assets.data[market.baseCurrency].displayPrecision
                )}{' '}
                {baseAsset}
              </Value>
              <Value name={t('common_created')}>{formatCreatedAt(order.data.events.createdAt)}</Value>
              {/* <Value name="Fee">0</Value> */}
              {order.data.commissions && (
                <Value name={t('common_payment_order_commission')}>
                  {formatAmount(getCommissionsSum(order.data), market.minTickLength)} {quoteAsset}
                </Value>
              )}
            </Column>
          </Row>
        </>
      ) : (
        <Row border={!isMobile && order.data.fills.length > 0} startAligned styles={[styles.row]}>
          <Column fd="column" sm={12}>
            <Value name={t('common_market')}>
              <MarketId pair={order.data.market} />
            </Value>
            <Value name={t('common_side')}>
              <Text
                styles={[
                  {
                    [styles.buy]: order.data.side === 'BUY',
                    [styles.sell]: order.data.side === 'SELL'
                  }
                ]}>
                {order.data.side === 'BUY' ? t('common_buy') : t('common_sell')}
              </Text>
            </Value>
            <Value name={t('common_amount')}>{formatAmount(order.data.quantity, market?.minQtyLength)}</Value>
            <Value name={t('common_filled')}>
              {formatAmount(getFillsQuantitySum(order.data), market?.minQtyLength)}
            </Value>
            <Value name={t('common_price')}>
              {order.data.execution.type === 'MARKET'
                ? t('common_market')
                : `${formatAmount(order.data.execution.price, market.minTickLength)}`}
            </Value>
            <Value name={t('common_created')}>
              <Text muted>{formatCreatedAt(order.data.events.createdAt)}</Text>
            </Value>
            {order.data.commissions && (
              <Value name={t('common_payment_order_commission')}>
                {formatAmount(getCommissionsSum(order.data), market.minTickLength)}
              </Value>
            )}
          </Column>
        </Row>
      )}

      {order.data.fills.length > 0 && (
        <>
          <Row styles={[styles.fillsRow]}>
            <Column lg={3} sm={12}>
              <Text weight={isMobile ? 'regular' : 'medium'} muted={isMobile}>
                {t('common_fills')}
              </Text>
            </Column>
            {!isMobile && (
              <Column lg={6}>
                <Text>{t('orders_order_was_filled_by')}</Text>
              </Column>
            )}
          </Row>

          <Row styles={[styles.tableRow]}>
            <Column lg={9} sm={12}>
              <Table
                columnWidths={columnWidths}
                data={mapFillsWithExtraData(order.data)}
                error={order.error}
                refetchData={order.refetch}
                isLoading={order.loading}
                endAlignIndex={1}
                headers={headers}
                renderItem={isMobile ? renderItemMobile : renderItem}
              />
            </Column>
          </Row>
        </>
      )}

      {isMobile && isOpenOrder(order.data.events) && (
        <Column sm={12} endAligned>
          <ButtonPrimary
            disabled={isNotCancellable}
            title={isNotCancellable ? t('orders_cannot_be_cancelled_until') : undefined}
            onClick={openCancelModal}
            styles={[styles.buttonCancel]}>
            {t('common_cancel_order')}
          </ButtonPrimary>
        </Column>
      )}

      {cancelModalOpen && (
        <ModalCancelOrder handleClose={closeCancelModal} order={order.data} goBack={goBack} />
      )}
    </div>
  );
}

OrderDetails.propTypes = {
  accountId: PropTypes.string.isRequired,
  match: PropTypes.object.isRequired
};

export default OrderDetails;
